import type { AdultAccidentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import {
    getCoverageTermValue,
    formatAmountWithCurrency,
    isCoverageSelected
} from '../coverableDetailsInsuranceSumsConfig';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import type { RowInformation, TableInformation } from '../../../../config/flavourConfig';

const getAdultAccidentInsuranceSumsCoverableDetailsConfig = (
    adultCoverable: AdultAccidentDTO
): TableInformation => {
    const beneficiaries: RowInformation[] = [];
    adultCoverable?.beneficiaries?.forEach((beneficiary) => {
        if (
            !beneficiary.fullName ||
            !beneficiary.cprNumber ||
            !isCoverageSelected(adultCoverable, 'ACCDeathCov')
        )
            return;
        beneficiaries.push({
            key: beneficiary.fullName,
            name: 'Person, du har valgt som begunstiget',
            fieldValue: beneficiary.fullName
        });
        beneficiaries.push({
            key: beneficiary.cprNumber,
            name: 'CPR-nummer på den begunstigede',
            fieldValue: beneficiary.cprNumber
        });
    });

    return {
        title: 'Forsikringssummer',
        rowsInformation: [
            {
                name: 'Varigt mén - erstatningen er en procentdel af forsikringssummen',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCPermanentInjuryToothDamageCov',
                        'ACCPermanentInjuryToothDamageCovSumInsured'
                    )
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovSumInsured'
                )
            },
            {
                name: 'Erstatningen starter ved méngrad på',
                fieldValue: `${getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCovPIDegree'
                )}%`,
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCovPIDegree'
                )
            },
            {
                name: 'Dobbelterstatning – (gælder til du fylder 70 år) starter ved',
                fieldValue: `${getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCompensation'
                )}%`,
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCompensation'
                )
            },
            {
                name: 'Tandskade',
                fieldValue: 'Efter regning',
                valueType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-EFTER-REGNING-TITLE',
                        description: 'DSS-ACC-ADULT-YOUNG-HIGHLIGHTED-TEXT-MODAL-EFTER-REGNING'
                    }
                }
            },
            {
                name: 'Udgifter til behandling',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCPermanentInjuryToothDamageCov',
                        'ACCPermanentInjuryToothDamageCovTreatmentCost'
                    ).split('.')[0]
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovTreatmentCost'
                )
            },
            {
                name: 'Skade på briller',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCPermanentInjuryToothDamageCov',
                        'ACCPermanentInjuryToothDamageCovGlasses'
                    )
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovGlasses'
                )
            },
            {
                name: 'Udbetaling til efterladte ved dødsfald ifm. ulykke',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(adultCoverable, 'ACCDeathCov', 'ACCDeathCovSumInsured')
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCDeathCov',
                    'ACCDeathCovSumInsured'
                )
            },
            {
                name: 'Straksudbetaling til de efterladte ved dødsfald ifm. ulykke',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(adultCoverable, 'ACCDeathCov', 'ACCDeathCovCompensation')
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCDeathCov',
                    'ACCDeathCovCompensation'
                )
            },
            ...beneficiaries,
            {
                name: 'Udbetaling ved enkelt brud eller overrivning',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCImmediateCompensationCov',
                        'ACCImmComCov1fracTopTerm'
                    )
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCImmediateCompensationCov',
                    'ACCImmComCov1fracTopTerm'
                )
            },
            {
                name: 'Udbetaling ved flere samtidige brud eller overrivninger',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCImmediateCompensationCov',
                        'ACCImmComCovMultiTopTerm'
                    )
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCImmediateCompensationCov',
                    'ACCImmComCovMultiTopTerm'
                )
            },
            {
                name: 'Varigt mén ved ulykke i forbindelse med farlig sport - erstatningen er en procentdel af forsikringssummen',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCHighRiskSportCov',
                        'ACCHighRiskSportCovHighRiskSportCovDisabilitCovTerm'
                    )
                ),
                valueType: 'amount',
                hidden: !getCoverageTermValue(
                    adultCoverable,
                    'ACCHighRiskSportCov',
                    'ACCHighRiskSportCovHighRiskSportCovDisabilitCovTerm'
                )
            },
            {
                name: 'Udbetaling til efterladte ved dødsfald ifm. ulykke ved farlig sport',
                fieldValue: formatAmountWithCurrency(
                    getCoverageTermValue(
                        adultCoverable,
                        'ACCHighRiskSportCov',
                        'ACCHighRiskSportCovHighRiskSportCovDeathCovTerm'
                    )
                ),
                valueType: 'amount',
                hidden:
                    !isCoverageSelected(adultCoverable, 'ACCHighRiskSportCov') ||
                    !isCoverageSelected(adultCoverable, 'ACCDeathCov')
            }
        ]
    };
};

export { getAdultAccidentInsuranceSumsCoverableDetailsConfig };
